import { v4 as uuid } from 'uuid';
import config from 'src/config';
import { AuthToken } from 'src/modules/auth/authToken';
import authAxios from 'src/modules/shared/axios/authAxios';

const SCHEMA_VERSION = 2;
const MAX_SESSION_DURATION_SECONDS = 5400;

type SessionSurface =
  | 'frequency'
  | 'meditation'
  | 'deep_dive'
  | 'fmf';

interface SessionState {
  sessionId: string;
  sessionStartedAt: string;
  sessionStartMs: number;
}

export interface UsageLogEventInput {
  localTimestamp: Date | string;
  type: string;
  sessionSurface?: SessionSurface;
  affirmationProgram?: string | number | null;
  affirmation?: string | number | null;
  soundTitle?: string | number | null;
  sound?: string | number | null;
  device?: string;
  payload?: unknown;
  meditationId?: string | number | null;
}

export interface UsageLogEventPayload {
  localTimestamp: string;
  type: string;
  affirmationProgram: string | number | null;
  affirmation: string | number | null;
  soundTitle: string | number | null;
  sound: string | number | null;
  device: string;
  payload: unknown | null;
  meditationId: string | number | null;
  schemaVersion: number;
  sessionId?: string;
  sessionStartedAt?: string;
  sessionDurationSeconds?: number;
}

function getSessionSurface(
  eventType: string,
  sessionSurface?: SessionSurface,
): SessionSurface | null {
  if (sessionSurface) {
    return sessionSurface;
  }

  if (eventType.startsWith('FREQUENCY_')) {
    return 'frequency';
  }
  if (eventType.startsWith('MEDITATION_')) {
    return 'meditation';
  }
  if (eventType.startsWith('FMF_')) {
    return 'fmf';
  }
  if (eventType.startsWith('DEEP_DIVE_')) {
    return 'deep_dive';
  }
  return null;
}

function isPlayEvent(eventType: string): boolean {
  return eventType.endsWith('_PLAY');
}

function isTerminalEvent(eventType: string): boolean {
  return (
    eventType.endsWith('_PAUSE') ||
    eventType.endsWith('_ABORT') ||
    eventType.endsWith('_ENDED')
  );
}

function closesSession(eventType: string): boolean {
  return (
    eventType.endsWith('_ABORT') || eventType.endsWith('_ENDED')
  );
}

function toIsoTimestamp(value: Date | string): string {
  if (value instanceof Date) {
    return value.toISOString();
  }
  return new Date(value).toISOString();
}

function computeSessionDurationSeconds(sessionStartMs: number): number {
  const seconds = Math.floor((Date.now() - sessionStartMs) / 1000);
  return Math.min(
    Math.max(0, seconds),
    MAX_SESSION_DURATION_SECONDS,
  );
}

function getAuthorizationHeader(): string | null {
  const token = AuthToken.get();
  const tokenResponse = localStorage.getItem('tokenResponse');
  const raw = token || tokenResponse;
  return raw ? `Bearer ${raw}` : null;
}

export default class UsageLogService {
  private static sessionTracker: Partial<
    Record<SessionSurface, SessionState>
  > = {};

  private static eventQueue: UsageLogEventPayload[] = [];

  private static sending = false;

  private static lifecycleListenersAttached = false;

  static hasValidToken(): boolean {
    const token = AuthToken.get();
    const tokenResponse = localStorage.getItem('tokenResponse');
    return Boolean(
      (token && String(token).trim()) ||
        (tokenResponse && tokenResponse.trim()),
    );
  }

  /** Clears sessions and queued events (e.g. on logout). */
  static clearSessions(): void {
    this.sessionTracker = {};
    this.clearQueue();
  }

  private static clearQueue(): void {
    this.eventQueue = [];
  }

  private static ensureLifecycleListeners(): void {
    if (this.lifecycleListenersAttached) {
      return;
    }

    this.lifecycleListenersAttached = true;

    const flushOnHide = () => {
      this.flushWithKeepalive();
    };

    window.addEventListener('beforeunload', flushOnHide);
    window.addEventListener('pagehide', flushOnHide);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flushOnHide();
      }
    });
  }

  private static applySessionFields(
    surface: SessionSurface,
    eventType: string,
  ): Record<string, string | number> {
    let session = this.sessionTracker[surface];

    if (isPlayEvent(eventType)) {
      if (!session) {
        session = {
          sessionId: uuid(),
          sessionStartedAt: new Date().toISOString(),
          sessionStartMs: Date.now(),
        };
        this.sessionTracker[surface] = session;
      }

      return {
        sessionId: session.sessionId,
        sessionStartedAt: session.sessionStartedAt,
      };
    }

    if (!isTerminalEvent(eventType) || !session) {
      return {};
    }

    const fields: Record<string, string | number> = {
      sessionId: session.sessionId,
      sessionStartedAt: session.sessionStartedAt,
      sessionDurationSeconds: computeSessionDurationSeconds(
        session.sessionStartMs,
      ),
    };

    if (closesSession(eventType)) {
      delete this.sessionTracker[surface];
    }

    return fields;
  }

  private static buildEvent(
    input: UsageLogEventInput,
  ): UsageLogEventPayload {
    const { type, sessionSurface } = input;
    const surface = getSessionSurface(type, sessionSurface);
    const sessionFields = surface
      ? this.applySessionFields(surface, type)
      : {};

    return {
      localTimestamp: toIsoTimestamp(input.localTimestamp),
      type,
      affirmationProgram: input.affirmationProgram ?? null,
      affirmation: input.affirmation ?? null,
      soundTitle: type.startsWith('MEDITATION_')
        ? null
        : input.soundTitle ?? null,
      sound: input.sound ?? null,
      device: input.device || 'WEB',
      payload: input.payload ?? null,
      meditationId: input.meditationId ?? null,
      schemaVersion: SCHEMA_VERSION,
      ...sessionFields,
    };
  }

  private static isValidEvent(event: UsageLogEventPayload): boolean {
    if (
      event.type.startsWith('FREQUENCY_') &&
      !event.soundTitle
    ) {
      return false;
    }

    if (
      event.type.startsWith('FMF_') &&
      !event.soundTitle
    ) {
      return false;
    }

    if (
      event.type.startsWith('MEDITATION_') &&
      !event.meditationId
    ) {
      return false;
    }

    return true;
  }

  private static async sendSingle(event: UsageLogEventPayload) {
    if (!this.hasValidToken()) {
      return;
    }

    const response = await authAxios.post(`/usage-log`, {
      data: event,
    });
    return response.data;
  }

  private static enqueue(event: UsageLogEventPayload): void {
    this.ensureLifecycleListeners();
    this.eventQueue.push(event);
    void this.flush();
  }

  private static getUsageLogUrl(): string {
    const base = config.backendUrl.replace(/\/$/, '');
    return `${base}/usage-log`;
  }

  static async flush(): Promise<void> {
    if (!this.hasValidToken()) {
      this.clearQueue();
      return;
    }

    if (this.eventQueue.length === 0) {
      return;
    }

    if (this.sending) {
      return;
    }

    this.sending = true;

    try {
      while (this.eventQueue.length > 0) {
        if (!this.hasValidToken()) {
          this.clearQueue();
          return;
        }

        const next = this.eventQueue[0];

        try {
          await this.sendSingle(next);
        } catch (error) {
          console.debug('usage-log failed', error);
        } finally {
          this.eventQueue.shift();
        }
      }
    } finally {
      this.sending = false;
    }
  }

  /** Best-effort flush when the tab is hidden or closing (needs Bearer auth). */
  private static flushWithKeepalive(): void {
    if (!this.hasValidToken() || this.eventQueue.length === 0) {
      return;
    }

    const authorization = getAuthorizationHeader();
    if (!authorization) {
      this.clearQueue();
      return;
    }

    const events = this.eventQueue.splice(0);

    try {
      events.forEach((event) => {
        fetch(this.getUsageLogUrl(), {
          method: 'POST',
          headers: {
            Authorization: authorization,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ data: event }),
          keepalive: true,
        }).catch((error) => console.debug('usage-log failed', error));
      });
    } catch (error) {
      console.debug('usage-log failed', error);
    }
  }

  static async create(input: UsageLogEventInput) {
    if (!this.hasValidToken()) {
      return;
    }

    const event = this.buildEvent(input);

    if (!this.isValidEvent(event)) {
      return;
    }

    this.enqueue(event);
  }
}
